import { Directive, ElementRef, HostListener, Renderer2 } from '@angular/core';

/** CCR1-15700
 * Formats the Indicative Data DOB input as MM/DD/YYYY,
 * accepts numeric input only, and preserves date sections during editing.
 */
@Directive({
  selector: 'input[appIndicativeDobFormat]'
})
export class IndicativeDobFormatDirective {
  // Prevents the input event triggered by this directive from being processed again.
  private isFormatting = false;

  /**
   * Gets the input element and renderer needed to update the displayed DOB. */
  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) { }

  /** CCR1-15700 
   * Checks an edit before the browser applies it so typed characters are digits only.
   * Sends Backspace and Delete to the slash-protection handler; paste and drop
   * are cleaned after insertion by onInput.
   */
  @HostListener('beforeinput', ['$event'])
  onBeforeInput(event: InputEvent): void {
    const inputType = event.inputType ?? '';

    // Handle Backspace and Delete before the browser changes the input value.
    if (
      inputType === 'deleteContentBackward' ||
      inputType === 'deleteContentForward'
    ) {
      this.handleDeletion(
        event,
        inputType === 'deleteContentBackward'
      );

      return;
    }

    // Paste and drop are sanitized by the input handler.
    if (
      inputType === 'insertFromPaste' ||
      inputType === 'insertFromDrop'
    ) {
      return;
    }

    // Ignore operations that do not insert text.
    if (!inputType.startsWith('insert')) {
      return;
    }

    const enteredValue = event.data ?? '';

    // Allow only digits. Slashes are inserted automatically.
    if (enteredValue && !/^[0-9]+$/.test(enteredValue)) {
      event.preventDefault();
    }
  }

  /**
   * Formats the displayed DOB after an edit, including paste and drop.
   * Keeps existing month, day, and year sections separate when slashes remain;
   * otherwise builds MM/DD/YYYY progressively from the first eight digits.
   */
  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    if (this.isFormatting) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    const slashCount =
      (currentValue.match(/\//g) ?? []).length;

    /*
     * Preserve month, day, and year sections when one or two slashes exist.
     * Otherwise, rebuild the value from the first eight digits.
     */
    const formattedValue =
      slashCount === 1 || slashCount === 2
        ? this.formatExistingSegments(currentValue)
        : this.formatNewDigits(
          this.getDigits(currentValue, 8)
        );

    if (currentValue !== formattedValue) {
      this.updateInputValue(formattedValue);
    }
  }

  /**
   * Stops Backspace or Delete from removing a slash and merging date sections.
   * Removes any affected digits while keeping slashes, and allows the entire field
   * to be cleared when all of its text is selected.
   */
  private handleDeletion(
    event: InputEvent,
    isBackspace: boolean
  ): void {
    const input = this.elementRef.nativeElement;
    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;

    if (
      selectionStart === null ||
      selectionEnd === null
    ) {
      return;
    }

    // Determine the range that Backspace or Delete will remove.
    const start =
      selectionStart === selectionEnd && isBackspace
        ? Math.max(0, selectionStart - 1)
        : selectionStart;

    const end =
      selectionStart === selectionEnd && !isBackspace
        ? Math.min(
          input.value.length,
          selectionEnd + 1
        )
        : selectionEnd;

    // Allow the complete DOB to be cleared normally.
    if (
      start === 0 &&
      end === input.value.length
    ) {
      return;
    }

    const deletedText =
      input.value.slice(start, end);

    // Allow normal deletion when no separator is affected.
    if (!deletedText.includes('/')) {
      return;
    }

    event.preventDefault();

    // Keep separators included in the selected range.
    const retainedSlashes =
      deletedText.replace(/[^/]/g, '');

    const updatedValue = (
      input.value.slice(0, start) +
      retainedSlashes +
      input.value.slice(end)
    );

    const requestedCaretPosition = isBackspace
      ? start
      : start + retainedSlashes.length;

    // Ensure the cursor does not extend beyond the updated value.
    const caretPosition = Math.min(
      requestedCaretPosition,
      updatedValue.length
    );

    this.updateInputValue(
      updatedValue,
      caretPosition
    );
  }

  /**
   * Cleans each part of a date that already has slashes independently.
   * This keeps year digits from moving into the day when a day digit is removed.
   */
  private formatExistingSegments(
    value: string
  ): string {
    const parts = value.split('/');

    // Sanitize each section separately so digits do not move across sections.
    const month = this.getDigits(
      parts[0] ?? '',
      2
    );

    const day = this.getDigits(
      parts[1] ?? '',
      2
    );

    const year = this.getDigits(
      parts[2] ?? this.getDigits(parts[1] ?? '', 6).slice(2),
      4
    );

    // Remove the trailing slash when the year is empty.
    if (parts.length === 3 && !year) {
      return `${month}/${day}`;
    }

    // Remove the trailing slash when the day is empty.
    if (parts.length === 2 && !day) {
      return month;
    }

    // Keep the partially entered day without adding another slash.
    if (parts.length === 2 && day.length < 2) {
      return `${month}/${day}`;
    }

    return `${month}/${day}/${year}`;
  }

  /**
   * Adds slashes as a new DOB is entered, after the month and then the day.
   * This lets an agent type eight digits without typing separators.
   */
  private formatNewDigits(
    digits: string
  ): string {
    // Keep the month unchanged until more than two digits are entered.
    if (digits.length <= 2) {
      return digits;
    }

    // Add the first slash after the month digits.
    if (digits.length <= 4) {
      return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    // Add both slashes once the year digits start.
    return (
      `${digits.slice(0, 2)}/` +
      `${digits.slice(2, 4)}/` +
      digits.slice(4, 8)
    );
  }

  /**
   * Removes non-digits and limits a date part to its allowed length.
   * Both the new-entry and existing-section formatters use the same rule.
   */
  private getDigits(
    value: string,
    maximumLength: number
  ): string {
    // Remove unsupported characters and apply the required digit limit.
    return value
      .replace(/\D/g, '')
      .slice(0, maximumLength);
  }

  /**
   * Displays a formatted DOB and sends an input event so Angular Material
   * and the reactive form receive it. Restores the cursor after that update.
   */
  private updateInputValue(
    value: string,
    caretPosition = value.length
  ): void {
    const input = this.elementRef.nativeElement;

    this.isFormatting = true;

    this.renderer.setProperty(
      input,
      'value',
      value
    );

    // Notify Angular Material and the reactive form about the new value.
    input.dispatchEvent(
      new Event('input', {
        bubbles: true
      })
    );

    this.isFormatting = false;

    // Restore the cursor after the directive updates the displayed value.
    requestAnimationFrame(() => {
      const safePosition = Math.min(
        caretPosition,
        input.value.length
      );

      input.setSelectionRange(
        safePosition,
        safePosition
      );
    });
  }
}